import React from "react";
import { FieldValues, get, Path, PathValue, RegisterOptions, useFormContext, useWatch } from "react-hook-form";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatAmount } from "@/util/sums";
import { Label } from "../ui/label";

// Fields for the public site's forms. They read the form from a FormProvider, so bind them to a
// form type once per form, e.g. `const TextField = BaseTextField<ContactFormData>`.

export interface Option {
  value: string;
  label: string;
}

interface FieldProps<T extends FieldValues> {
  name: Path<T>;
  label: React.ReactNode;
  helper?: string;
  className?: string;
}

const controlClass = (hasError: boolean) =>
  cn(
    "h-14 w-full rounded-[9px] border bg-white px-4 text-base text-navy transition-[color,box-shadow] outline-none placeholder:text-slate-400 focus-visible:ring-3 md:text-[15px]",
    hasError
      ? "border-red-400 focus-visible:ring-red-200"
      : "border-[#d5e0e3] focus-visible:border-brand focus-visible:ring-brand/25",
  );

const useField = <T extends FieldValues>(name: Path<T>, helper?: string) => {
  const {
    register,
    setValue,
    trigger,
    formState: { errors },
  } = useFormContext<T>();
  const error: string | undefined = get(errors, name)?.message;

  // Re-check a field that shows an error on every change, so fixing it clears the message
  // while typing instead of on blur, which would shift the layout under the Continue click.
  const field = (options: RegisterOptions<T, Path<T>> = {}) =>
    register(name, { ...options, onChange: () => error && trigger(name) });

  return {
    field,
    setValue,
    error,
    ariaProps: {
      id: name,
      "aria-invalid": !!error,
      "aria-describedby": error ? `${name}-error` : helper ? `${name}-helper` : undefined,
    },
  };
};

const FieldShell = ({
  name,
  label,
  helper,
  error,
  meta,
  className,
  children,
}: {
  name: string;
  label: React.ReactNode;
  helper?: string;
  error?: string;
  meta?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}) => {
  const note = error ? (
    <p id={`${name}-error`} className="text-sm text-red-600">
      {error}
    </p>
  ) : (
    helper && (
      <p id={`${name}-helper`} className="text-[13px] text-slate-500">
        {helper}
      </p>
    )
  );

  return (
    <div className={className}>
      <Label htmlFor={name} className="text-[15px] font-semibold text-navy">
        {label}
      </Label>
      <div className="mt-2">{children}</div>
      {(note || meta) && (
        <div className="mt-1.5 flex items-start justify-between gap-4">
          {note || <span />}
          {meta}
        </div>
      )}
    </div>
  );
};

export const TextField = <T extends FieldValues>({
  name,
  label,
  helper,
  className,
  ...inputProps
}: FieldProps<T> & Omit<React.InputHTMLAttributes<HTMLInputElement>, "name">) => {
  const { field, error, ariaProps } = useField<T>(name, helper);

  return (
    <FieldShell name={name} label={label} helper={helper} error={error} className={className}>
      <input className={controlClass(!!error)} {...inputProps} {...ariaProps} {...field()} />
    </FieldShell>
  );
};

export const TextAreaField = <T extends FieldValues>({
  name,
  label,
  helper,
  className,
  maxLength,
  ...textAreaProps
}: FieldProps<T> & Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "name">) => {
  const { field, error, ariaProps } = useField<T>(name, helper);
  const length = String(useWatch<T>({ name }) ?? "").length;

  return (
    <FieldShell
      name={name}
      label={label}
      helper={helper}
      error={error}
      meta={
        maxLength && (
          <p className="shrink-0 text-[13px] text-slate-500 tabular-nums">
            {length.toLocaleString()} / {maxLength.toLocaleString()}
          </p>
        )
      }
      className={className}
    >
      <textarea
        maxLength={maxLength}
        className={cn(controlClass(!!error), "h-auto resize-y py-3.5 leading-relaxed")}
        {...textAreaProps}
        {...ariaProps}
        {...field()}
      />
    </FieldShell>
  );
};

export const AmountField = <T extends FieldValues>({
  name,
  label,
  helper,
  className,
  placeholder,
}: FieldProps<T> & { placeholder: string }) => {
  const { field, setValue, error, ariaProps } = useField<T>(name, helper);

  return (
    <FieldShell name={name} label={label} helper={helper} error={error} className={className}>
      <input
        inputMode="numeric"
        placeholder={placeholder}
        className={controlClass(!!error)}
        {...ariaProps}
        {...field({
          onBlur: (event) => setValue(name, formatAmount(event.target.value) as PathValue<T, Path<T>>),
        })}
      />
    </FieldShell>
  );
};

export const SelectField = <T extends FieldValues>({
  name,
  label,
  options,
  placeholder = "Select an option",
  className,
}: FieldProps<T> & { options: Option[]; placeholder?: string }) => {
  const { field, error, ariaProps } = useField<T>(name);

  return (
    <FieldShell name={name} label={label} error={error} className={className}>
      <div className="relative">
        <select
          required
          className={cn(controlClass(!!error), "cursor-pointer appearance-none pr-11 invalid:text-slate-400")}
          {...ariaProps}
          {...field()}
        >
          <option value="" disabled>
            {placeholder}
          </option>
          {options.map((option) => (
            <option key={option.value} value={option.value} className="text-navy">
              {option.label}
            </option>
          ))}
        </select>
        <ChevronDown
          className="pointer-events-none absolute top-1/2 right-4 size-4 -translate-y-1/2 text-slate-500"
          aria-hidden
        />
      </div>
    </FieldShell>
  );
};

export const ConsentField = <T extends FieldValues>({
  name,
  children,
}: {
  name: Path<T>;
  children: React.ReactNode;
}) => {
  const { field, error, ariaProps } = useField<T>(name);

  return (
    <div>
      <label
        htmlFor={name}
        className="flex cursor-pointer items-start gap-3 rounded-xl border border-[#e0efec] bg-[#f2fbf9] p-4 text-[15px] leading-snug text-navy"
      >
        <input type="checkbox" className="mt-0.5 size-4 shrink-0 accent-brand-ink" {...ariaProps} {...field()} />
        <span>{children}</span>
      </label>
      {error && (
        <p id={`${name}-error`} className="mt-1.5 text-sm text-red-600">
          {error}
        </p>
      )}
    </div>
  );
};
